"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createReducer = createReducer;
exports.useSelectorMemo = useSelectorMemo;
exports.createSelector = createSelector;
exports.createActor = createActor;
Object.defineProperty(exports, "schema", {
  enumerable: true,
  get: function get() {
    return _schema["default"];
  }
});
Object.defineProperty(exports, "record", {
  enumerable: true,
  get: function get() {
    return _record["default"];
  }
});
Object.defineProperty(exports, "createHydrator", {
  enumerable: true,
  get: function get() {
    return _hydrator["default"];
  }
});

var _react = require("react");

var _schema = _interopRequireDefault(require("./schema"));

var _record = _interopRequireDefault(require("./record"));

var _hydrator = _interopRequireDefault(require("./hydrator"));

var _shallowEqual = _interopRequireDefault(require("./shallowEqual"));

var _destructure = _interopRequireDefault(require("./destructure"));

var _arrayEqual = _interopRequireDefault(require("./arrayEqual"));

function _interopRequireDefault(obj) {
  return obj && obj.__esModule ? obj : {
    "default": obj
  };
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _iterableToArrayLimit(arr, i) {
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
} // Shamelessly copied comment from:
//    https://github.com/reduxjs/react-redux/blob/316467a07e29911d82ba0342364a907e05d9066c/src/hooks/useSelector.js#L6
// React currently throws a warning when using useLayoutEffect on the server.
// To get around it, we can conditionally useEffect on the server (no-op) and
// useLayoutEffect in the browser. We need useLayoutEffect to ensure the store
// subscription callback always has the selector from the latest render commit
// available, otherwise a store update may happen between render and the effect,
// which may cause missed updates; we also must ensure the store subscription
// is created synchronously, otherwise a store update may occur before the
// subscription is created and an inconsistent state may be observed


var useIsomorphicLayoutEffect = typeof window !== 'undefined' ? _react.useLayoutEffect : _react.useEffect;

function createReducer(structure) {
  return (0, _destructure["default"])(structure, function (item) {
    return item.getReducer();
  });
}

function useSelectorMemo(fn) {
  var cache = (0, _react.useRef)(null);
  return function memo() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (cache.current && (0, _arrayEqual["default"])(args, cache.current.args)) return cache.current.value;
    var value = fn.apply(void 0, args);
    cache.current = {
      args: args,
      value: value
    };
    return value;
  };
}

function createSelector(structure, store) {
  var selector = (0, _destructure["default"])(structure, function (item, key) {
    var getState = function getState() {
      return store.getState()[key];
    };

    return item.getSelector(getState);
  }); // With useEffect, the subscription happens for the child first and then
  // the parent. This would create an unintended behaviour when the state
  // needs to be updated. When a state tree changes a parent component
  // should be updated prior to the children. Hence a suscriber is created
  // on a per instance basis

  var SelectorInstance =
  /*#__PURE__*/
  function () {
    function SelectorInstance() {
      var _this = this;

      _classCallCheck(this, SelectorInstance);

      this.cache = null;
      this.listener = undefined;
      this.unsubscribe = store.subscribe(function () {
        if (_this.listener) _this.listener();
      });

      this.memoize = function (fn, dependencies) {
        if (_this.cache && (0, _arrayEqual["default"])(_this.cache.dependencies, dependencies)) {
          return _this.cache.value;
        }

        _this.cache = {
          dependencies: dependencies,
          value: fn()
        };
        return _this.cache.value;
      };
    }

    _createClass(SelectorInstance, [{
      key: "subscribe",
      value: function subscribe(listener) {
        var _this2 = this; // run the listener when only dependency changes


        if (this.listener === null) listener();
        this.listener = listener;
        return function () {
          _this2.listener = null;
        };
      }
    }]);

    return SelectorInstance;
  }();

  return function useSelector(mapState) {
    var dependencies = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    var instance = (0, _react.useRef)(null);

    if (instance.current === null) {
      instance.current = new SelectorInstance();
    }

    var _useState = (0, _react.useState)(function () {
      selector.memoize = instance.current.memoize;
      return mapState(selector, dependencies);
    }),
        _useState2 = _slicedToArray(_useState, 2),
        state = _useState2[0],
        setState = _useState2[1]; // useEffect uses the state value to initialize the prevValue without
    // keeping it in dependency, which is safe.


    useIsomorphicLayoutEffect(function () {
      var prevValue = state;

      function updateState() {
        selector.memoize = instance.current.memoize;
        var newValue = mapState(selector, dependencies);

        if (!(0, _shallowEqual["default"])(newValue, prevValue)) {
          prevValue = newValue;
          setState(newValue);
        }
      }

      return instance.current.subscribe(updateState);
    }, dependencies); // An unmount effect

    useIsomorphicLayoutEffect(function () {
      return instance.current.unsubscribe;
    }, []);
    return state;
  };
}

function createActor(structure, dispatch) {
  return (0, _destructure["default"])(structure, function (item) {
    return item.getActor(dispatch);
  });
}